"use client";

import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";
import { loadOneTrustScript } from "../../../utils/oneTrust.ts";

/**
 * Defers OneTrust SDK loading until the URL has a resolved locale prefix.
 *
 * This avoids two issues with loading the script eagerly in main.tsx:
 *   - On "/", the locale isn't known yet (the router redirects to the
 *     preferred locale via ForwardToLocaleRoute), so a path-based check
 *     would either always load or always skip.
 *   - Once loaded, the OneTrust SDK can't be easily removed,
 *     so we should only add it once we know the final locale.
 *
 * The script is loaded at most once per page lifetime, and is skipped
 * entirely for English locales (e.g. en-IN, en-US, ...).
 */
export function OneTrustLoader(): null {
  const loadedRef = useRef(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (loadedRef.current) return;

    const pathLocale = pathname.split("/")[1]?.toLowerCase();
    if (!pathLocale) return;
    if (pathLocale.startsWith("en")) return;

    loadedRef.current = true;
    loadOneTrustScript();
  }, [pathname]);

  return null;
}
